import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BadgeCheck, Check, Clock, Coins, Dices, HelpCircle, Landmark, Loader2, Minus, Plus, RotateCcw, Ticket, Trash2, Trophy, X } from 'lucide-react';
import { useT } from '../../i18n';
import Spinner from '../../components/UI/Spinner';
import { targetChain } from '../../.config';
import { explorerTx } from '../../utils';
import useBestiarium from './useBestiarium';
import { ANIMALS, MODALITIES, animalArt, animalOf, isAllPrizes, modalityOf, pad } from './animals';
import './bestiarium.css';
import banner from '../../assets/bestiarium/banner.webp';

const MAX_SLIP = 20;
const QUICK_STAKES = [1, 5, 10, 50];
const SEEN_KEY = 'bestiarium-seen-draw';
const LOCAL_CHAIN = 31337;
// the modality picker: single numbers first, then the combinations
const MODALITY_GROUPS = [
  { key: 'simple', items: ['grupo', 'dezena', 'centena', 'milhar'] },
  { key: 'combo', items: ['duqueGrupo', 'ternoGrupo', 'duqueDezena', 'ternoDezena'] },
];
const CONFETTI_COLORS = ['#fcd34d', '#fbbf24', '#fde68a', '#c9acea', '#8e3fcc', '#ffffff'];

const fmt = (value, digits = 2) =>
  Number.isFinite(value) ? value.toLocaleString('en-US', { maximumFractionDigits: digits }) : '-';

const reducedMotion = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const readSeenDraw = () => {
  try {
    const value = localStorage.getItem(SEEN_KEY);
    return value === null ? null : Number(value);
  } catch (err) {
    return null;
  }
};

const writeSeenDraw = (id) => {
  try {
    localStorage.setItem(SEEN_KEY, String(id));
  } catch (err) {
    // private mode: the draw just animates again next time
  }
};

/** Wallet and network errors in the player's words */
const txErrorText = (err, t) => {
  if (err?.reverted) return t('bestiarium.tx.reverted');
  const message = err?.shortMessage || err?.message || '';
  if (/reject|denied|cancel/i.test(message)) return t('bestiarium.tx.rejected');
  return message.split('\n')[0].slice(0, 160);
};

/** "≈ $12.34": dollar value of a CRANNI amount at the live price, nothing while the price is unknown */
const Usd = ({ cranni, price, className = '' }) => {
  if (!price || !Number.isFinite(cranni) || cranni <= 0) return null;
  const value = cranni * price;
  const text = value < 0.01 ? '< $0.01' : `$${value.toLocaleString('en-US', { minimumFractionDigits: value < 1000 ? 2 : 0, maximumFractionDigits: 2 })}`;
  return <span className={`notranslate lb-num text-white/40 ${className}`}>≈ {text}</span>;
};

/** Countdown to `target` (unix seconds) on the chain clock */
const Countdown = ({ target, offset }) => {
  const [now, setNow] = useState(() => (Date.now() + offset) / 1000);
  useEffect(() => {
    const id = setInterval(() => setNow((Date.now() + offset) / 1000), 1000);
    return () => clearInterval(id);
  }, [offset]);
  const left = Math.max(0, Math.floor(target - now));
  const h = Math.floor(left / 3600);
  const m = Math.floor((left % 3600) / 60);
  const s = left % 60;
  return (
    <span className="lb-num">
      {h > 0 ? `${h}:` : ''}
      {pad(m, 2)}:{pad(s, 2)}
    </span>
  );
};

/** The animal's card art, or its emoji while the art is missing */
const AnimalImg = ({ group, className = 'h-6 w-6', emojiClass = 'text-xl', t }) => {
  const art = animalArt(group);
  const name = t(`bestiarium.animal.${group}`);
  return art ? (
    <img src={art} alt={name} title={name} loading="lazy" className={`rounded-md object-cover ${className}`} />
  ) : (
    <span className={emojiClass} role="img" aria-label={name}>
      {ANIMALS[group - 1].emoji}
    </span>
  );
};

/** One animal tile: card art, name and its four dezenas; a picked animal turns gold */
const AnimalTile = ({ animal, selected, onClick, t }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    className={`relative flex flex-col items-center rounded-xl border px-1 py-2 transition duration-200 ${
      selected
        ? 'border-amber-300/90 bg-amber-400/10 shadow-[0_0_0_1px_rgba(252,211,77,0.55),0_0_26px_-6px_rgba(252,211,77,0.7)]'
        : 'border-white/10 bg-white/[0.03] hover:-translate-y-0.5 hover:border-white/30 hover:bg-white/[0.06]'
    }`}
  >
    {selected ? (
      <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-amber-300 text-black shadow" aria-hidden="true">
        <Check className="h-3 w-3" strokeWidth={3} />
      </span>
    ) : null}
    <AnimalImg group={animal.group} t={t} className="aspect-square w-full max-w-[84px]" emojiClass="text-3xl" />
    <span className={`mt-0.5 text-[10px] font-semibold ${selected ? 'text-amber-200' : 'text-white/85'}`}>{t(`bestiarium.animal.${animal.group}`)}</span>
    <span className={`lb-num whitespace-nowrap text-[8.5px] tracking-tight sm:text-[10px] sm:tracking-normal ${selected ? 'text-amber-200/70' : 'text-white/55'}`}>{animal.dezenas.map((d) => pad(d, 2)).join(' ')}</span>
  </button>
);

/** Readable description of a selection: animals or zero-padded numbers */
const SelectionLabel = ({ betType, numbers, t }) => {
  const modality = modalityOf(betType);
  if (modality.digits === 0) {
    return (
      <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
        {numbers.map((g) => (
          <span key={g} className="inline-flex items-center gap-1">
            <AnimalImg group={g} t={t} className="h-5 w-5" emojiClass="text-sm" />
            {t(`bestiarium.animal.${g}`)}
          </span>
        ))}
      </span>
    );
  }
  return <span className="lb-num">{numbers.map((n) => pad(n, modality.digits)).join(' · ')}</span>;
};

/** The 5 prizes of a draw with their animals */
const DrawNumbers = ({ numbers, t, highlight = [] }) => (
  <ol className="grid grid-cols-5 gap-1.5">
    {numbers.map((n, i) => {
      const animal = animalOf(n);
      return (
        <li key={i} className={`rounded-lg border px-1 py-1.5 text-center ${highlight.includes(i) ? 'border-amber-300/60 bg-amber-400/10' : 'border-white/10 bg-black/30'}`}>
          <div className="text-[9px] font-semibold text-white/40">{i + 1}º</div>
          <div className="lb-num text-sm font-bold text-white">{pad(n, 4)}</div>
          <div className="mt-1 flex justify-center leading-none">
            <AnimalImg group={animal.group} t={t} className="h-9 w-9" emojiClass="text-base" />
          </div>
        </li>
      );
    })}
  </ol>
);

/**
 * The latest draw, revealed like a slot machine the first time this browser sees it (or on replay): the five numbers
 * spin and stop one by one, each animal card turning face up. `onRevealed(drawId)` runs when the last one lands.
 */
const RevealDraw = ({ draw, replay, onRevealed, t }) => {
  const [shown, setShown] = useState(5);
  const [animating, setAnimating] = useState(false);
  const [rolling, setRolling] = useState([0, 0, 0, 0, 0]);
  const animateFor = useRef({}); // draw id -> animate, decided once (StrictMode runs effects twice)
  const revealedRef = useRef(onRevealed);
  revealedRef.current = onRevealed;
  const drawId = draw?.id;

  useEffect(() => {
    if (drawId === undefined) return undefined;
    if (animateFor.current[drawId] === undefined) animateFor.current[drawId] = readSeenDraw() !== drawId;
    writeSeenDraw(drawId);
    if ((!animateFor.current[drawId] && !replay) || reducedMotion()) {
      setShown(5);
      setAnimating(false);
      return undefined;
    }
    setShown(0);
    setAnimating(true);
    const spin = setInterval(() => setRolling([0, 1, 2, 3, 4].map(() => Math.floor(Math.random() * 10000))), 70);
    const stops = [0, 1, 2, 3, 4].map((i) => setTimeout(() => setShown(i + 1), 900 + i * 650));
    const done = setTimeout(() => {
      clearInterval(spin);
      setAnimating(false);
      revealedRef.current?.(drawId);
    }, 900 + 4 * 650 + 700);
    return () => {
      clearInterval(spin);
      stops.forEach(clearTimeout);
      clearTimeout(done);
    };
  }, [drawId, replay]);

  if (!draw) return null;
  return (
    <ol className="grid grid-cols-5 gap-1.5">
      {draw.numbers.map((n, i) => {
        const landed = i < shown;
        const animal = animalOf(n);
        return (
          <li
            key={i}
            className={`rounded-lg border px-1 py-1.5 text-center transition-colors ${
              i === 0 && landed ? 'border-amber-300/60 bg-amber-400/10' : 'border-white/10 bg-black/30'
            }`}
          >
            <div className="text-[9px] font-semibold text-white/40">{i + 1}º</div>
            <div className="lb-num text-sm font-bold">
              {landed ? (
                <span key={`n${drawId}-${replay}`} className={`${animating ? 'bst-land' : ''} text-white`}>
                  {pad(n, 4)}
                </span>
              ) : (
                <span className="bst-rolling text-white/35">{pad(rolling[i], 4)}</span>
              )}
            </div>
            <div className="mt-1 flex h-9 justify-center leading-none [perspective:400px]">
              {landed ? (
                <span key={`a${drawId}-${replay}`} className={`${animating ? 'bst-flip-in' : ''} inline-flex`}>
                  <AnimalImg group={animal.group} t={t} className="h-9 w-9" emojiClass="text-base" />
                </span>
              ) : (
                <span className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 bg-white/5 text-sm font-bold text-white/30">?</span>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

/** Gold confetti over the screen, once per `burst` */
const Confetti = ({ burst }) => {
  const pieces = useMemo(
    () =>
      [...Array(70).keys()].map((i) => ({
        left: Math.random() * 100,
        delay: Math.random() * 0.6,
        duration: 2.2 + Math.random() * 1.6,
        size: 6 + Math.random() * 6,
        drift: (Math.random() - 0.5) * 160,
        spin: 360 + Math.random() * 720,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      })),
    // a new set of pieces for every burst
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [burst]
  );
  if (!burst) return null;
  return (
    <div className="pointer-events-none fixed inset-0 z-[60] overflow-hidden" aria-hidden="true">
      {pieces.map((p, i) => (
        <span
          key={i}
          className="bst-confetti"
          style={{
            left: `${p.left}%`,
            width: p.size,
            height: p.size * 0.45,
            background: p.color,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            '--drift': `${p.drift}px`,
            '--spin': `${p.spin}deg`,
          }}
        />
      ))}
    </div>
  );
};

/** Modalities in two rows (single numbers, combinations), each with its payout multiplier */
const ModalityPicker = ({ value, onChange, multipliers, t }) => (
  <div className="mt-4 space-y-3">
    {MODALITY_GROUPS.map((group) => (
      <div key={group.key}>
        <p className="lb-label mb-1.5">{t(`bestiarium.modGroup.${group.key}`)}</p>
        <div className="grid grid-cols-4 gap-1 rounded-xl border border-white/10 bg-black/30 p-1">
          {group.items.map((key) => {
            const m = MODALITIES.find((x) => x.key === key);
            const active = value === key;
            const multiplier = multipliers[m.head];
            return (
              <button
                key={key}
                type="button"
                onClick={() => onChange(key)}
                aria-pressed={active}
                className={`rounded-lg px-1 py-1.5 text-center transition ${
                  active ? 'bg-brand text-white shadow-[0_6px_22px_-8px_rgba(142,63,204,0.95)]' : 'text-white/65 hover:bg-white/5 hover:text-white'
                }`}
              >
                <span className="block text-[11px] font-semibold leading-tight sm:text-xs">{t(`bestiarium.modality.${key}`)}</span>
                <span className={`lb-num mt-0.5 block text-[11px] font-bold ${active ? 'text-amber-200' : 'text-amber-300/80'}`}>
                  {multiplier ? `${fmt(multiplier)}×` : '-'}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    ))}
  </div>
);

/** The pick being built, large: one slot per animal or number the modality takes */
const PickPreview = ({ modality, slots, t }) => (
  <div className="flex flex-wrap gap-2">
    {slots.map((value, i) => {
      if (value === null) {
        return (
          <div key={i} className="flex h-[92px] w-[76px] flex-col items-center justify-center rounded-xl border border-dashed border-white/15 text-lg font-bold text-white/25">
            ?
          </div>
        );
      }
      const group = modality.digits === 0 ? value : animalOf(value).group;
      return (
        <div
          key={`${i}-${value}`}
          className="bst-pop flex w-[76px] flex-col items-center rounded-xl border border-amber-300/60 bg-amber-400/10 px-1 py-1.5 shadow-[0_0_22px_-8px_rgba(252,211,77,0.7)]"
        >
          <AnimalImg group={group} t={t} className="h-12 w-12" emojiClass="text-3xl leading-[3rem]" />
          {modality.digits === 0 ? (
            <>
              <span className="mt-0.5 truncate text-[10px] font-semibold text-amber-200">{t(`bestiarium.animal.${group}`)}</span>
              <span className="lb-num text-[9px] text-white/50">{ANIMALS[group - 1].dezenas.map((d) => pad(d, 2)).join(' ')}</span>
            </>
          ) : (
            <>
              <span className="lb-num mt-0.5 text-sm font-bold text-white">{pad(value, modality.digits)}</span>
              <span className="truncate text-[9px] text-white/50">{t(`bestiarium.animal.${group}`)}</span>
            </>
          )}
        </div>
      );
    })}
  </div>
);

/** Transaction progress in the page's own style: approve, bet or claim, each signed, then confirmed on-chain */
const TxSteps = ({ tx, t, onRetry, onClose }) => {
  const current = tx.steps.indexOf(tx.step);
  const link = tx.hash && targetChain?.id !== LOCAL_CHAIN ? explorerTx(tx.hash) : null;
  return (
    <div className="bst-pop mt-3 rounded-2xl border border-white/10 bg-black/40 p-4" role="status" aria-live="polite">
      <ol className="space-y-3">
        {tx.steps.map((step, i) => {
          const state = tx.phase === 'done' || i < current ? 'done' : i === current ? (tx.phase === 'error' ? 'error' : 'active') : 'todo';
          return (
            <li key={step} className="flex items-start gap-3">
              <span
                className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[11px] font-bold ${
                  state === 'done'
                    ? 'border-emerald-400/60 bg-emerald-400/15 text-emerald-300'
                    : state === 'error'
                      ? 'border-rose-400/60 bg-rose-400/15 text-rose-300'
                      : state === 'active'
                        ? 'border-[#c9acea]/70 bg-brand/25 text-[#e4d6f4]'
                        : 'border-white/15 text-white/35'
                }`}
              >
                {state === 'done' ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : state === 'error' ? <X className="h-3.5 w-3.5" strokeWidth={3} /> : state === 'active' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : i + 1}
              </span>
              <div className="min-w-0">
                <p className={`text-sm font-semibold ${state === 'todo' ? 'text-white/40' : 'text-white'}`}>{t(`bestiarium.tx.${step}`)}</p>
                {state === 'active' ? <p className="text-xs text-white/50">{t(tx.phase === 'wallet' ? 'bestiarium.tx.wallet' : 'bestiarium.tx.pending')}</p> : null}
                {state === 'error' ? <p className="break-words text-xs text-rose-300">{tx.error}</p> : null}
              </div>
            </li>
          );
        })}
      </ol>
      {tx.phase === 'done' ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-emerald-400/30 bg-emerald-400/10 px-3 py-2">
          <p className="flex items-center gap-2 text-sm font-semibold text-emerald-200">
            <BadgeCheck className="h-4 w-4" aria-hidden="true" /> {t(tx.kind === 'claim' ? 'bestiarium.tx.claimed' : 'bestiarium.betPlaced')}
          </p>
          {link ? (
            <a href={link} target="_blank" rel="noopener noreferrer" className="lb-link text-xs">
              {t('bestiarium.tx.view')}
            </a>
          ) : null}
        </div>
      ) : null}
      {tx.phase === 'done' || tx.phase === 'error' ? (
        <div className="mt-3 flex gap-2">
          {tx.phase === 'error' ? (
            <button type="button" onClick={onRetry} className="lb-btn lb-btn-primary lb-btn-sm flex-1">
              {t('bestiarium.tx.retry')}
            </button>
          ) : null}
          <button type="button" onClick={onClose} className="lb-btn lb-btn-outline lb-btn-sm flex-1">
            {t('bestiarium.tx.close')}
          </button>
        </div>
      ) : null}
    </div>
  );
};

const heroBackgroundStyle = {
  backgroundColor: '#000000',
  backgroundImage: `url(${banner})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
};

const Hero = ({ t }) => (
  <section className="relative overflow-hidden">
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 select-none" style={heroBackgroundStyle}>
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-black/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-black/50" />
    </div>
    <div className="relative mx-auto w-full max-w-screen-2xl px-4 py-10 sm:px-8 sm:py-14 lg:px-10">
      <span className="lb-chip lb-chip-gold">
        <span className="lb-dot" aria-hidden="true" />
        {t('bestiarium.badge')}
      </span>
      <h1 className="lb-heading mt-5 text-[2.25rem] leading-[0.98] sm:text-5xl lg:text-6xl">
        <span className="lb-gold-text">Cranni Bestiarium</span>
        <span className="mt-1 block text-2xl sm:text-3xl">{t('bestiarium.subtitle')}</span>
      </h1>
      <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/75 sm:text-base">{t('bestiarium.body')}</p>
      <dl className="mt-7 grid max-w-xl grid-cols-3 gap-3">
        {['draws', 'prizes', 'fair'].map((k) => (
          <div key={k} className="rounded-2xl border border-white/10 bg-black/40 px-3 py-2.5">
            <dt className="lb-label">{t(`bestiarium.pillar.${k}`)}</dt>
            <dd className="mt-1 text-xs font-semibold text-white sm:text-sm">{t(`bestiarium.pillar.${k}Value`)}</dd>
          </div>
        ))}
      </dl>
    </div>
  </section>
);

const Bestiarium = () => {
  const t = useT();
  const game = useBestiarium();
  const price = game.cranniUsd;
  const [modalityKey, setModalityKey] = useState('grupo');
  const [allPrizes, setAllPrizes] = useState(false);
  const [picked, setPicked] = useState([]); // groups (animals) or numbers
  const [digits, setDigits] = useState(['', '', '']);
  const [stake, setStake] = useState('1');
  const [slip, setSlip] = useState([]);
  const [room, setRoom] = useState(null); // prize still available on the selection in this draw
  const [notice, setNotice] = useState(null);
  const [tx, setTx] = useState(null); // { kind, steps, step, phase: wallet | pending | done | error, error, hash }
  const [replay, setReplay] = useState(0);
  const [celebration, setCelebration] = useState(null); // { id, amount }
  const [burst, setBurst] = useState(0);
  const slipRef = useRef(null);
  const myBetsRef = useRef(game.myBets);
  myBetsRef.current = game.myBets;

  const modality = MODALITIES.find((m) => m.key === modalityKey);
  const betType = modality.all !== undefined && allPrizes ? modality.all : modality.head;
  const multiplier = game.multipliers[betType] || 0;
  const numbers = useMemo(() => {
    if (modality.digits === 0) return picked;
    const values = digits.slice(0, modality.count).map((v) => (v === '' ? null : Number(v)));
    return values.every((v) => v !== null && v < 10 ** modality.digits) ? values : [];
  }, [modality, picked, digits]);
  // one slot per number the modality takes, filled as the player picks or types
  const slots = [...Array(modality.count).keys()].map((i) => {
    if (modality.digits === 0) return picked[i] ?? null;
    return digits[i] && digits[i].length === modality.digits ? Number(digits[i]) : null;
  });
  const complete = numbers.length === modality.count && new Set(numbers).size === numbers.length;
  const stakeValue = Number(stake) || 0;
  const prize = stakeValue * multiplier;
  const maxStake = room !== null && multiplier ? room / multiplier : null;
  const slipTotal = slip.reduce((sum, b) => sum + b.stake, 0);
  const toClaim = game.myBets.filter((b) => b.settled && !b.claimed && b.payout > 0);
  const toClaimTotal = toClaim.reduce((sum, b) => sum + b.payout, 0);
  const txBusy = tx !== null && (tx.phase === 'wallet' || tx.phase === 'pending');

  const { remainingPayout } = game;
  useEffect(() => {
    let active = true;
    setRoom(null);
    if (complete) remainingPayout(betType, numbers).then((value) => active && setRoom(value));
    return () => {
      active = false;
    };
  }, [complete, betType, numbers, remainingPayout, game.drawId]);

  useEffect(() => setNotice(null), [betType, numbers, stake]);

  // a finished transaction panel closes by itself
  useEffect(() => {
    if (tx?.phase !== 'done') return undefined;
    const id = setTimeout(() => setTx(null), 6000);
    return () => clearTimeout(id);
  }, [tx]);

  useEffect(() => {
    if (!burst) return undefined;
    const id = setTimeout(() => setBurst(0), 4500);
    return () => clearTimeout(id);
  }, [burst]);

  const celebrate = () => {
    if (!reducedMotion()) setBurst(Date.now());
  };

  // the reveal of a new draw finished: celebrate the connected wallet's prizes in it
  const onRevealed = (drawId) => {
    const won = myBetsRef.current.filter((b) => b.drawId === drawId && b.payout > 0).reduce((sum, b) => sum + b.payout, 0);
    if (won > 0) {
      setCelebration({ id: drawId, amount: won });
      celebrate();
    }
  };

  const changeModality = (key) => {
    setModalityKey(key);
    setPicked([]);
    setDigits(['', '', '']);
  };

  const toggleAnimal = (group) => {
    setPicked((list) => {
      if (list.includes(group)) return list.filter((g) => g !== group);
      if (modality.count === 1) return [group];
      return list.length < modality.count ? [...list, group] : list;
    });
  };

  const addToSlip = () => {
    if (!complete || stakeValue < game.minBet) return;
    if (maxStake !== null && stakeValue > maxStake) {
      setNotice(t('bestiarium.limitText', { max: fmt(maxStake, 4) }));
      return;
    }
    if (slip.length >= MAX_SLIP) return;
    setSlip((list) => [...list, { betType, numbers: [...numbers], stake: stakeValue, prize }]);
    setPicked([]);
    setDigits(['', '', '']);
  };

  const update = (patch) => setTx((state) => (state ? { ...state, ...patch } : state));

  const submit = async () => {
    if (!slip.length || txBusy) return;
    const steps = game.allowance < slipTotal ? ['approve', 'bet'] : ['bet'];
    setTx({ kind: 'bet', steps, step: steps[0], phase: 'wallet', error: null, hash: null });
    try {
      if (steps[0] === 'approve') {
        await game.approve(slipTotal, () => update({ phase: 'pending' }));
        update({ step: 'bet', phase: 'wallet' });
      }
      const hash = await game.placeBets(slip, () => update({ phase: 'pending' }));
      update({ phase: 'done', hash });
      setSlip([]);
    } catch (err) {
      update({ phase: 'error', error: txErrorText(err, t) });
    }
  };

  const claimAll = async () => {
    if (!toClaim.length || txBusy) return;
    setTx({ kind: 'claim', steps: ['claim'], step: 'claim', phase: 'wallet', error: null, hash: null });
    try {
      const hash = await game.claim(
        toClaim.map((b) => b.id),
        () => update({ phase: 'pending' })
      );
      update({ phase: 'done', hash });
      setCelebration(null);
      celebrate();
    } catch (err) {
      update({ phase: 'error', error: txErrorText(err, t) });
    }
  };

  if (game.loading) {
    return (
      <div className="py-24">
        <Spinner size="lg" label={t('bestiarium.loading')} />
      </div>
    );
  }

  const last = game.results[0];
  const claimButton = (
    <button type="button" disabled={txBusy} onClick={claimAll} className="lb-btn lb-btn-primary lb-btn-sm bst-claim-glow">
      <Trophy className="mr-1 h-4 w-4" /> {t('bestiarium.claimAll', { amount: fmt(toClaimTotal) })}
    </button>
  );

  return (
    <div className="lb-page w-full">
      <Confetti burst={burst} />
      <Hero t={t} />
      <div className="mx-auto w-full max-w-screen-2xl space-y-6 px-3 pb-28 sm:px-8 lg:px-10 lg:pb-16">
        {game.error === 'not-deployed' ? (
          <div className="lb-card p-6 text-center text-sm text-white/60">{t('bestiarium.notDeployed')}</div>
        ) : null}

        {/* status: next draw, bankroll, last result */}
        <section className="grid gap-3 md:grid-cols-[1fr_1fr_1.6fr]">
          <div className="lb-tile flex items-center gap-3 p-4">
            <Clock className="h-6 w-6 text-[#c9acea]" aria-hidden="true" />
            <div>
              <p className="lb-label">{t('bestiarium.nextDraw', { id: game.drawId })}</p>
              <p className="font-display text-2xl text-white">
                <Countdown target={game.closeTime} offset={game.chainOffset} />
              </p>
            </div>
          </div>
          <div className="lb-tile flex items-center gap-3 p-4">
            <Landmark className="h-6 w-6 text-amber-300" aria-hidden="true" />
            <div>
              <p className="lb-label">{t('bestiarium.bankroll')}</p>
              <p className="lb-num font-display text-2xl text-white">
                {fmt(game.bankroll, 0)} <span className="text-sm text-white/50">CRANNI</span>
              </p>
              <Usd cranni={game.bankroll} price={price} className="text-xs" />
            </div>
          </div>
          <div className="lb-tile p-4">
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="lb-label">{last ? t('bestiarium.lastResult', { id: last.id }) : t('bestiarium.noResults')}</p>
              {last ? (
                <button type="button" onClick={() => setReplay((n) => n + 1)} className="lb-link inline-flex items-center gap-1 text-[11px]">
                  <RotateCcw className="h-3 w-3" aria-hidden="true" /> {t('bestiarium.replay')}
                </button>
              ) : null}
            </div>
            {last ? <RevealDraw draw={last} replay={replay} onRevealed={onRevealed} t={t} /> : null}
          </div>
        </section>

        {celebration ? (
          <section className="bst-pop relative overflow-hidden rounded-2xl border border-amber-300/50 bg-gradient-to-r from-amber-500/20 via-amber-300/10 to-transparent p-4 sm:p-5">
            <div className="flex flex-wrap items-center gap-4">
              <Trophy className="h-9 w-9 shrink-0 text-amber-300" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="lb-label text-amber-200/80">{t('bestiarium.win.title', { id: celebration.id })}</p>
                <p className="font-display text-2xl">
                  <span className="lb-gold-text">{fmt(celebration.amount)} CRANNI</span> <Usd cranni={celebration.amount} price={price} className="text-sm" />
                </p>
              </div>
              {toClaim.length ? claimButton : null}
              <button type="button" onClick={() => setCelebration(null)} className="text-white/50 hover:text-white" aria-label={t('bestiarium.tx.close')}>
                <X className="h-5 w-5" />
              </button>
            </div>
          </section>
        ) : null}

        <div className="grid items-start gap-6 lg:grid-cols-[1.5fr_1fr]">
          {/* ---------- bet builder ---------- */}
          <section className="rounded-3xl border-2 border-brand/50 bg-gradient-to-b from-[#170c22] to-[#06030a] p-5 sm:p-6">
            <h2 className="lb-heading text-2xl text-white">{t('bestiarium.makeBet')}</h2>

            <ModalityPicker value={modalityKey} onChange={changeModality} multipliers={game.multipliers} t={t} />

            {modality.all !== undefined ? (
              <div className="mt-3 inline-flex rounded-lg border border-white/10 bg-black/30 p-0.5 text-xs">
                {[false, true].map((all) => (
                  <button
                    key={String(all)}
                    type="button"
                    onClick={() => setAllPrizes(all)}
                    aria-pressed={allPrizes === all}
                    className={`rounded-md px-3 py-1.5 font-semibold ${allPrizes === all ? 'bg-white/15 text-white' : 'text-white/50 hover:text-white/80'}`}
                  >
                    {t(all ? 'bestiarium.allPrizes' : 'bestiarium.head')}
                    <span className="lb-num ml-1.5 text-amber-300/90">
                      {fmt(game.multipliers[all ? modality.all : modality.head])}×{all ? ` ${t('bestiarium.perHit')}` : ''}
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
            <p className="mt-2 text-xs text-white/50">
              {t(`bestiarium.rule.${modality.key}`)} {modality.all !== undefined ? t(allPrizes ? 'bestiarium.rule.all' : 'bestiarium.rule.head') : ''}
            </p>

            {/* selection */}
            <div className="mt-4">
              {modality.digits === 0 ? (
                <>
                  <p className="lb-label mb-2">{t('bestiarium.pickAnimals', { n: modality.count })}</p>
                  <div className="grid grid-cols-5 gap-1.5">
                    {ANIMALS.map((a) => (
                      <AnimalTile key={a.group} animal={a} t={t} selected={picked.includes(a.group)} onClick={() => toggleAnimal(a.group)} />
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <p className="lb-label mb-2">{t('bestiarium.pickNumbers', { n: modality.count, digits: modality.digits })}</p>
                  <div className="flex flex-wrap gap-2">
                    {[...Array(modality.count).keys()].map((i) => (
                      <input
                        key={i}
                        inputMode="numeric"
                        maxLength={modality.digits}
                        value={digits[i]}
                        placeholder={'0'.repeat(modality.digits)}
                        aria-label={`${t(`bestiarium.modality.${modality.key}`)} ${i + 1}`}
                        onChange={(e) => {
                          const v = e.target.value.replace(/\D/g, '').slice(0, modality.digits);
                          setDigits((list) => list.map((x, k) => (k === i ? v : x)));
                        }}
                        className="lb-input notranslate lb-num w-28 text-center text-2xl font-bold"
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* stake and the pick summary */}
            <div className="mt-5 grid gap-4 sm:grid-cols-[1fr_1.1fr]">
              <div>
                <label className="lb-label mb-2 block" htmlFor="bestiarium-stake">
                  {t('bestiarium.stake')}
                </label>
                <div className="flex items-stretch gap-2">
                  <button type="button" onClick={() => setStake(String(Math.max(game.minBet, stakeValue - 1)))} className="lb-btn lb-btn-outline px-3" aria-label="-1">
                    <Minus className="h-4 w-4" />
                  </button>
                  <div className="relative flex-1">
                    <input
                      id="bestiarium-stake"
                      inputMode="decimal"
                      value={stake}
                      onChange={(e) => setStake(e.target.value.replace(',', '.').replace(/[^\d.]/g, ''))}
                      className="lb-input notranslate lb-num w-full pr-20 text-xl font-bold"
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-white/40">CRANNI</span>
                  </div>
                  <button type="button" onClick={() => setStake(String(stakeValue + 1))} className="lb-btn lb-btn-outline px-3" aria-label="+1">
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                <Usd cranni={stakeValue} price={price} className="mt-1 block text-xs" />
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {QUICK_STAKES.map((v) => (
                    <button key={v} type="button" onClick={() => setStake(String(v))} className="lb-chip notranslate hover:border-white/30">
                      {v}
                    </button>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-black/30 p-3 text-sm">
                <p className="lb-label mb-2">{t('bestiarium.yourPick')}</p>
                <PickPreview modality={modality} slots={slots} t={t} />
                <div className="mt-3 border-t border-white/10 pt-2">
                  <div className="flex justify-between gap-2">
                    <span className="text-white/50">{t('bestiarium.multiplier')}</span>
                    <span className="lb-num font-bold text-white">{fmt(multiplier)}×</span>
                  </div>
                  <div className="mt-1 flex justify-between gap-2">
                    <span className="text-white/50">{t(isAllPrizes(betType) ? 'bestiarium.prizePerHit' : 'bestiarium.prize')}</span>
                    <span className="text-right">
                      <span className="lb-num block font-bold text-amber-300">{fmt(prize)} CRANNI</span>
                      <Usd cranni={prize} price={price} className="block text-xs" />
                    </span>
                  </div>
                  {maxStake !== null ? (
                    <div className="mt-1 flex justify-between gap-2 text-xs">
                      <span className="text-white/40">{t('bestiarium.maxStake')}</span>
                      <span className="lb-num text-white/70">{fmt(maxStake, 4)}</span>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={addToSlip}
              disabled={!complete || stakeValue < game.minBet || slip.length >= MAX_SLIP}
              className="lb-btn lb-btn-primary lb-btn-lg mt-5 w-full"
            >
              <Plus className="mr-1 h-4 w-4" /> {t('bestiarium.addToSlip')}
            </button>
            {notice ? (
              <p className="bst-pop mt-2 rounded-xl border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-xs text-amber-200" role="alert">
                <span className="font-semibold">{t('bestiarium.limitTitle')}:</span> {notice}
              </p>
            ) : null}
            {stakeValue > 0 && stakeValue < game.minBet ? (
              <p className="mt-2 text-center text-xs text-amber-200">{t('bestiarium.minBet', { min: fmt(game.minBet, 4) })}</p>
            ) : null}
          </section>

          {/* ---------- slip: follows the page on large screens ---------- */}
          <aside ref={slipRef} className="scroll-mt-24 lg:sticky lg:top-24">
            <section className="lb-card p-5">
              <h3 className="lb-heading mb-3 flex items-center gap-2 text-lg">
                <Ticket className="h-5 w-5 text-[#c9acea]" aria-hidden="true" /> {t('bestiarium.slip')}
                {slip.length ? <span className="lb-num ml-auto rounded-full bg-brand/30 px-2 py-0.5 text-xs text-[#e4d6f4]">{slip.length}</span> : null}
              </h3>
              {slip.length ? (
                <ul className="lb-scroll max-h-[45vh] space-y-2 overflow-y-auto pr-1">
                  {slip.map((b, i) => (
                    <li key={`${i}-${b.betType}-${b.numbers.join('.')}`} className="bst-pop flex items-center justify-between gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm">
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase text-white/45">{t(`bestiarium.type.${b.betType}`)}</p>
                        <p className="truncate text-white">
                          <SelectionLabel betType={b.betType} numbers={b.numbers} t={t} />
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="lb-num font-bold text-white">{fmt(b.stake, 4)}</p>
                        <p className="lb-num text-[11px] text-amber-300">→ {fmt(b.prize)}</p>
                      </div>
                      <button
                        type="button"
                        disabled={txBusy}
                        onClick={() => setSlip((list) => list.filter((_, k) => k !== i))}
                        className="text-white/40 hover:text-white disabled:opacity-30"
                        aria-label={t('bestiarium.remove')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-white/45">{t('bestiarium.slipEmpty')}</p>
              )}
              <div className="mt-4 flex items-center justify-between text-sm">
                <span className="text-white/50">{t('bestiarium.total')}</span>
                <span className="text-right">
                  <span className="lb-num block font-bold text-white">{fmt(slipTotal, 4)} CRANNI</span>
                  <Usd cranni={slipTotal} price={price} className="block text-xs" />
                </span>
              </div>
              {game.isConnected ? (
                <>
                  {tx?.kind === 'bet' ? (
                    <TxSteps tx={tx} t={t} onRetry={submit} onClose={() => setTx(null)} />
                  ) : (
                    <button type="button" onClick={submit} disabled={!slip.length || txBusy || slipTotal > game.balance} className="lb-btn lb-btn-primary lb-btn-lg mt-3 w-full">
                      {slipTotal > game.balance ? t('bestiarium.noBalance') : t('bestiarium.placeBets', { amount: fmt(slipTotal, 4) })}
                    </button>
                  )}
                  <p className="mt-2 text-center text-xs text-white/40">
                    {t('bestiarium.balance', { amount: fmt(game.balance, 2) })} <Usd cranni={game.balance} price={price} />
                  </p>
                </>
              ) : (
                <div className="mt-3 flex flex-col items-center gap-2 rounded-xl border border-white/10 bg-black/30 p-3">
                  <p className="text-xs text-white/55">{t('bestiarium.connect')}</p>
                  <appkit-button />
                </div>
              )}
            </section>
          </aside>
        </div>

        {/* ---------- my bets, results and rules ---------- */}
        <div className="grid items-start gap-6 lg:grid-cols-2">
          {game.isConnected ? (
            <section className="lb-card p-5">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h3 className="lb-heading flex items-center gap-2 text-lg">
                  <Dices className="h-5 w-5 text-[#c9acea]" aria-hidden="true" /> {t('bestiarium.myBets')}
                </h3>
                {toClaim.length && tx?.kind !== 'claim' ? claimButton : null}
              </div>
              {tx?.kind === 'claim' ? <TxSteps tx={tx} t={t} onRetry={claimAll} onClose={() => setTx(null)} /> : null}
              {game.myBets.length ? (
                <ul className="lb-scroll mt-3 max-h-96 space-y-2 overflow-y-auto pr-1">
                  {game.myBets.map((b) => (
                    <li
                      key={b.id}
                      className={`flex items-center justify-between gap-2 rounded-xl border px-3 py-2 text-sm ${
                        b.settled && b.payout > 0 && !b.claimed ? 'border-amber-300/40 bg-amber-400/[0.06]' : 'border-white/10 bg-white/[0.03]'
                      }`}
                    >
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase text-white/45">
                          #{b.drawId} · {t(`bestiarium.type.${b.betType}`)}
                        </p>
                        <p className="truncate text-white">
                          <SelectionLabel betType={b.betType} numbers={b.numbers} t={t} />
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="lb-num text-white/70">{fmt(b.stake, 4)}</p>
                        <p className={`text-[11px] font-semibold ${!b.settled ? 'text-white/45' : b.payout > 0 ? 'text-emerald-300' : 'text-white/35'}`}>
                          {!b.settled
                            ? t('bestiarium.waiting')
                            : b.payout > 0
                              ? `${t(b.claimed ? 'bestiarium.paid' : 'bestiarium.won')} ${fmt(b.payout)}`
                              : t('bestiarium.lost')}
                        </p>
                        {b.settled && b.payout > 0 ? <Usd cranni={b.payout} price={price} className="block text-[10px]" /> : null}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-white/45">{t('bestiarium.noBets')}</p>
              )}
            </section>
          ) : null}

          <section className="lb-card p-5">
            <h3 className="lb-heading mb-3 flex items-center gap-2 text-lg">
              <Coins className="h-5 w-5 text-amber-300" aria-hidden="true" /> {t('bestiarium.results')}
            </h3>
            {game.results.length ? (
              <ul className="space-y-3">
                {game.results.map((d) => (
                  <li key={d.id}>
                    <p className="lb-label mb-1.5">{t('bestiarium.drawLabel', { id: d.id })}</p>
                    <DrawNumbers numbers={d.numbers} t={t} highlight={[0]} />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-white/45">{t('bestiarium.noResults')}</p>
            )}
          </section>

          <details className="lb-card group" open>
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-5">
              <h3 className="lb-heading flex items-center gap-2 text-lg">
                <HelpCircle className="h-5 w-5 text-[#c9acea]" aria-hidden="true" /> {t('bestiarium.howItWorks')}
              </h3>
              <span className="text-white/40 transition-transform group-open:rotate-180" aria-hidden="true">
                ▾
              </span>
            </summary>
            <div className="space-y-3 px-5 pb-5 text-sm text-white/65">
              <p>{t('bestiarium.how1')}</p>
              <p>{t('bestiarium.how2')}</p>
              <p>{t('bestiarium.how3')}</p>
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="text-white/40">
                    <th className="py-1 font-normal">{t('bestiarium.modalityLabel')}</th>
                    <th className="py-1 text-right font-normal">{t('bestiarium.head')}</th>
                    <th className="py-1 text-right font-normal">{t('bestiarium.allPrizes')}</th>
                  </tr>
                </thead>
                <tbody>
                  {MODALITIES.map((m) => (
                    <tr key={m.key} className="border-t border-white/5 text-white/80">
                      <td className="py-1.5">{t(`bestiarium.modality.${m.key}`)}</td>
                      <td className="lb-num py-1.5 text-right">{m.all !== undefined ? `${fmt(game.multipliers[m.head])}×` : '-'}</td>
                      <td className="lb-num py-1.5 text-right">
                        {m.all !== undefined ? `${fmt(game.multipliers[m.all])}× ${t('bestiarium.perHit')}` : `${fmt(game.multipliers[m.head])}×`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-white/45">{t('bestiarium.how4', { cap: fmt(game.maxPayout, 0) })}</p>
            </div>
          </details>
        </div>
      </div>

      {/* phones: the slip total and a shortcut to it while the slip has bets */}
      {slip.length ? (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-[#08050d]/95 px-4 py-3 backdrop-blur lg:hidden">
          <div className="mx-auto flex max-w-xl items-center justify-between gap-3">
            <div>
              <p className="text-xs text-white/50">{t('bestiarium.slipCount', { n: slip.length })}</p>
              <p className="lb-num font-bold text-white">
                {fmt(slipTotal, 4)} CRANNI <Usd cranni={slipTotal} price={price} className="text-xs font-normal" />
              </p>
            </div>
            <button type="button" onClick={() => slipRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="lb-btn lb-btn-primary">
              <Ticket className="mr-1 h-4 w-4" /> {t('bestiarium.viewSlip')}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Bestiarium;
